import { EM_DASH } from '../../lib/typography';
import { Component } from 'preact';
import { TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED } from '../../lib/medievalTheme';
import { GAME_MODES, stakesLabel } from '../../utils/arena/gameModes';
import { IconTrash } from '../ui/icons';
import MatchupDisplay from './MatchupDisplay';
import { t } from '../../localization/t';

function modeTitle(key) {
  return GAME_MODES.find((m) => m.key === key)?.title || key;
}

function visibilityLabel(v) {
  if (!v) return '';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

// 72h grace-period client check. Mirrors server-side graceEligibleFor.
const GRACE_MS = 72 * 60 * 60 * 1000;
function graceEligible(instance, currentUserId) {
  if (instance.phase !== 'paused') return false;
  const others = (instance.participants || []).filter((p) => p.userId !== currentUserId);
  if (others.length === 0) return false;
  const cutoff = Date.now() - GRACE_MS;
  return others.every((p) => {
    const last = p.lastSeenAt ? new Date(p.lastSeenAt).getTime() : 0;
    return last < cutoff;
  });
}

function phaseHint(instance, currentUserId) {
  const me = (instance.participants || []).find((p) => p.userId === currentUserId);
  const isHost = me?.role === 'creator';
  if (graceEligible(instance, currentUserId)) {
    return t('Opponent inactive. Claim win available');
  }
  switch (instance.phase) {
    case 'gathering': {
      const count = (instance.participants || []).length;
      const need = (instance.seats_min || 2) - count;
      if (need > 0) return need === 1 ? t('Waiting for 1 more player') : t('Waiting for {n} more players', { n: need });
      return t('Ready {dash} waiting for ready-check', { dash: EM_DASH });
    }
    case 'active':
      return isHost ? t('In progress') : t('Currently playing');
    case 'paused':
      return isHost ? t('Paused. Click to resume') : t('Paused. Waiting for host');
    case 'completed': {
      const result = instance.result || {};
      if (result.forfeit) return result.winner === currentUserId ? t('Won by forfeit') : t('Forfeited');
      if (result.winner === currentUserId) return t('You won');
      if (result.winner) return t('You lost');
      return t('Completed');
    }
    default:
      return instance.phase;
  }
}

function primaryAction(instance, currentUserId) {
  const me = (instance.participants || []).find((p) => p.userId === currentUserId);
  const isHost = me?.role === 'creator';
  if (graceEligible(instance, currentUserId)) {
    return { label: t('Claim Win'), action: 'claim-win', emphasis: 'gold' };
  }
  switch (instance.phase) {
    case 'gathering':
      return { label: isHost ? t('Manage') : t('Open'), action: 'open', emphasis: 'bevel' };
    case 'active':
      return { label: isHost ? t('Return') : t('Rejoin'), action: 'rejoin', emphasis: 'gold' };
    case 'paused':
      if (isHost) return { label: t('Resume'), action: 'resume', emphasis: 'gold' };
      return { label: t('Ping Host'), action: 'request-resume', emphasis: 'bevel' };
    case 'completed':
      return { label: t('View Results'), action: 'view-result', emphasis: 'bevel' };
    default:
      return null;
  }
}

export default class SaveRow extends Component {
  handleClick = () => {
    const { instance, currentUserId, onAction } = this.props;
    const action = primaryAction(instance, currentUserId);
    if (!action) return;
    onAction?.(instance, action.action);
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleClick();
    }
  };

  handleDiscard = (e) => {
    e.stopPropagation();
    const { onAction, instance } = this.props;
    onAction?.(instance, 'discard');
  };

  render() {
    const { instance, currentUserId, sorceryCards } = this.props;
    const me = (instance.participants || []).find((p) => p.userId === currentUserId);
    const action = primaryAction(instance, currentUserId);
    const canDiscard = !!me && instance.phase !== 'completed' && instance.phase !== 'abandoned';

    const stakes = stakesLabel(instance.mode, instance.ranked_opt_in);
    const visibilityMode = `${t(visibilityLabel(instance.visibility))} ${t(modeTitle(instance.mode))}${stakes ? ` · ${t(stakes)}` : ''}`;

    return (
      <div
        className="vh-save-row group"
        role={action ? 'button' : undefined}
        tabIndex={action ? 0 : undefined}
        onClick={action ? this.handleClick : undefined}
        onKeyDown={action ? this.handleKeyDown : undefined}
        aria-label={action ? `${action.label}. ${this.props.savedName || visibilityMode}` : undefined}
      >
        <div className="flex flex-col gap-2 min-w-0">
          {/* Headline: either the custom save name (host-set via "Save
              As…") or just the visibility + mode combo. The matchup
              card below carries the per-player identity. */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className="text-sm font-semibold arena-heading"
              style={{
                color: TEXT_PRIMARY,
                textShadow: '0 -1px 0 rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.5)',
              }}
            >
              {this.props.savedName || visibilityMode}
            </span>
            {this.props.savedName ? (
              <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>
                {visibilityMode}
              </span>
            ) : null}
          </div>
          <MatchupDisplay
            participants={instance.participants || []}
            seatsMax={instance.seats_max}
            creatorId={instance.creator_id}
            sorceryCards={sorceryCards}
          />
          <div className="text-xs italic" style={{ color: TEXT_BODY, opacity: 0.85 }}>
            {phaseHint(instance, currentUserId)}
          </div>
        </div>
        {/* Discard: hover-only trash icon. Owners (creators) abandon
            the instance server-side; joined guests leave it (gathering
            frees their seat, an unfinished match is conceded). Kept in
            the DOM at all times (opacity toggle) so row layout doesn't
            shift on hover. */}
        {canDiscard ? (
          <button
            type="button"
            className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-2 rounded cursor-pointer"
            style={{ color: TEXT_MUTED }}
            onClick={this.handleDiscard}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#e8d5a0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; }}
            title={me.role === 'creator'
              ? t('Abandon this save for everyone')
              : (instance.phase === 'gathering'
                ? t('Leave this lobby')
                : t('Forfeit and remove this match'))}
            aria-label={t('Discard')}
          >
            <IconTrash size={18} />
          </button>
        ) : null}
      </div>
    );
  }
}
